import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  authorizeExpiryEpochSec,
  buildAuthorizeUrl,
  DEFAULT_TOKEN_LIFETIME_SEC,
  formatRoute,
  loginRoute,
  parseAuthorizeCallback,
  queryParam,
  stripAuthCallbackParams,
  VISIOUS_SCOPES,
  withQuery,
  withRenewalScope,
  type AuthCallbackProblem,
} from '@visious/core';
import { AUTH_QUERY_KEY, fetchSubscriptions, type AuthApiProblem } from '../../api/auth';
import { describeAuthProblem, type ProblemCopy } from '../../api/problem';
import { text } from '../../strings';
import { getPlatform } from '../../platform/instance';
import {
  nowEpochSec,
  refreshSession,
  sessionInstance,
  signOutLocally,
  storeGrant,
  useSession,
  type SessionInstance,
} from '../../platform/session';
import { navigate } from '../../router/router';
import { loginView, type LoginView, type VerifyOutcome } from './model';

/**
 * The React binding for §8's sign-in page: `location`, `history`, `sessionStorage`, the token store and
 * the one authenticated call that proves the session works. Every *decision* is in `model.ts` and in
 * core (R13) — on Android the redirect arrives as a deep link and only this file is rewritten.
 *
 * It is the flow already proved, with three things added: a place to come back to, a verification
 * whose failure is reported in words rather than as a raw status, and a session store so the rest of
 * the app hears about it.
 */

/**
 * Set immediately before the top-level navigation to the consent screen, read once on the way back.
 *
 * It carries the two things the return trip cannot otherwise know. **That a grant was started at all**
 * — §8's denial is a bare redirect to the callback with no parameters whatsoever, so "no token" is the
 * only signal the reader said no, and without this flag it is indistinguishable from someone opening
 * `/login` directly. And **which instance the grant belongs to**, because a token filed against the
 * wrong instance answers `Invalid signature` on every call, which reads as tampering rather than as a
 * mix-up.
 *
 * Deliberately not under `visious.token.` — R9's `clearOtherInstances()` deletes every key in *that*
 * namespace on boot, so a flag parked there would be gone before the return trip could read it.
 */
const PENDING_KEY = 'visious.login.pending';

interface PendingGrant {
  readonly instanceOrigin: string;
  /** Where the reader was heading when a gate sent them here. */
  readonly next: string | null;
  /**
   * A one-time value echoed through the instance and required back — OAuth's `state`, for the reason
   * OAuth has it. Without it (and without the record itself) a **crafted link is a sign-in**: anyone
   * could send `…/login?token=<their own token>` and this page would file a stranger's credential
   * under the instance's key, so the victim would be signed into the attacker's account and every
   * later watch, subscription and history write would land there. Nothing about the token itself says
   * whose it is, and the instance is happy to serve it.
   *
   * `null` when this browser has no CSPRNG to build one; the record's existence still has to be
   * proved, so the crafted-link case is refused either way.
   */
  readonly state: string | null;
  /** When the grant was started, so a record left behind by an abandoned one cannot authorise a token. */
  readonly startedAtEpochSec: number;
}

/**
 * How long a started grant stays valid. Fifteen minutes is longer than any real consent takes (it is
 * two clicks on a page that is already open) and short enough that a record from an abandoned attempt
 * has expired by the time anyone could make use of it.
 */
const PENDING_TTL_SEC = 15 * 60;

/** 128 bits of `crypto.getRandomValues`, hex — unguessable and short enough to sit in a URL. */
function newState(): string | null {
  try {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

function readPending(): PendingGrant | null {
  try {
    const raw = globalThis.sessionStorage.getItem(PENDING_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    const origin = record['instanceOrigin'];
    const next = record['next'];
    const state = record['state'];
    return {
      instanceOrigin: typeof origin === 'string' ? origin : '',
      next: typeof next === 'string' && next !== '' ? next : null,
      state: typeof state === 'string' && state !== '' ? state : null,
      startedAtEpochSec: typeof record['startedAtEpochSec'] === 'number' ? record['startedAtEpochSec'] : 0,
    };
  } catch {
    // A blocked or partitioned context loses the denial hint. That is a worse page, not a broken one.
    return null;
  }
}

function writePending(value: PendingGrant): void {
  try {
    globalThis.sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
  } catch {
    /* see readPending */
  }
}

function clearPending(): void {
  try {
    globalThis.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Where the instance sends the reader back: `/login`, carrying **only** the `state`.
 *
 * The route can carry a `?next=` and this deliberately does not use it. A callback URL is handed to
 * *another program* to build a redirect from, so the less it has to carry the better — and "where to
 * return to" already travels in the session record, which has to exist anyway. `state` is the one thing
 * that cannot travel that way: its whole purpose is to prove the token in the URL belongs to the grant
 * this tab started, so it has to make the round trip through the instance.
 *
 * That the instance *does* echo it is measured rather than assumed (API reference §8): it parses the
 * callback's own query and merges into it, and its denial link is the callback verbatim. An instance
 * that appended a second `?` instead would break the match and the grant would be refused — which is
 * the safe direction for a check like this to fail in.
 */
function callbackUrl(state: string | null): string {
  const base = `${globalThis.location.origin}${formatRoute(loginRoute())}`;
  return state === null ? base : `${base}?state=${state}`;
}

/**
 * What the callback carried, read **during the first render** — see the note in {@link useLogin}.
 *
 * **R9: no credential in here.** This object goes into React state and is therefore one step from a
 * devtools inspection, an error boundary's state dump or a serialised log. It says *that* a token
 * arrived; the token itself is handed over separately, once, through {@link takeCapturedGrant}.
 */
interface ArrivedGrant {
  readonly hasToken: boolean;
  readonly username: string | null;
  readonly problem: AuthCallbackProblem | null;
  readonly state: string | null;
  readonly pending: PendingGrant | null;
}

/**
 * The credential's one-shot handoff from the render that read the URL to the effect that stores it.
 *
 * Module-scoped rather than a `useRef` (reading a ref during render is both a lint error and a real
 * hazard) and rather than state (R9). Cleared by the taker, so the grant cannot be consumed twice —
 * which is the same guarantee the effect's own guard provides, from the other end.
 */
let capturedGrant: { token: string; username: string | null; href: string } | null = null;

function takeCapturedGrant(): { token: string; username: string | null; href: string } | null {
  const captured = capturedGrant;
  capturedGrant = null;
  return captured;
}

function readArrival(): ArrivedGrant {
  const href = globalThis.location.href;
  const pending = readPending();
  const state = queryParam(href, 'state');
  const grant = parseAuthorizeCallback(href);
  if (grant.ok) {
    capturedGrant = { token: grant.value.token, username: grant.value.username, href };
    return { hasToken: true, username: grant.value.username, problem: null, state, pending };
  }
  return { hasToken: false, username: null, problem: grant.error, state, pending };
}

/**
 * The callback URL with everything the grant added taken off it — the credential (R9) and the `state`,
 * which has done its job by the time it is read. `canonicalHref` would drop both anyway, since no route
 * arm carries them, but a page that handles a credential should not depend on another mechanism to
 * finish the job.
 */
function strippedHref(href: string): string {
  return withQuery(stripAuthCallbackParams(href), { state: null }) ?? stripAuthCallbackParams(href);
}

/**
 * Does this callback belong to a grant *this tab* started? Refusing when it does not is what turns a
 * crafted `…/login?token=<somebody else's token>` link from a silent account swap into a message.
 */
function grantIsSolicited(arrival: ArrivedGrant, nowSec: number): boolean {
  const pending = arrival.pending;
  if (pending === null || pending.instanceOrigin === '') return false;
  if (pending.startedAtEpochSec <= 0 || nowSec - pending.startedAtEpochSec > PENDING_TTL_SEC) return false;
  // A record without a state is only possible where `crypto` is unavailable; the record itself is then
  // the whole proof, which still cannot be forged from another origin.
  return pending.state === null || pending.state === arrival.state;
}

export interface LoginHandlers {
  /** A **top-level navigation**: the consent screen is the instance's own page, so no CORS is involved. */
  readonly signIn: () => void;
  readonly signOut: () => void;
  readonly recheck: () => void;
  /** The renewal option (2026-08-02) — widens the *next* grant, never the current one. */
  readonly setAutoRenew: (on: boolean) => void;
}

export interface LoginBinding {
  readonly view: LoginView;
  readonly busy: boolean;
  readonly handlers: LoginHandlers;
}

export function useLogin(next: string | null): LoginBinding {
  const session = useSession();
  const queryClient = useQueryClient();

  /*
   * Read during the *first render*, not in an effect, and that is the one ordering that matters on this
   * page. `useCanonicalUrl` in `Routed` also rewrites the address bar — no route arm carries `token`,
   * so canonicalising strips it — and effects run children-first, so the two are a race that this read
   * simply does not enter.
   *
   * A `useState` initializer rather than a ref, and not only because reading a ref during render is a
   * lint error: this value is *rendered*, so state is what it is. It is safe to run twice (StrictMode
   * does) because reading `location.href` is pure — everything that writes is in the effect below.
   * The **credential** is not in it: `readArrival` parks that in a module-scoped one-shot (R9).
   */
  const [arrival] = useState<ArrivedGrant>(readArrival);

  const [instance, setInstance] = useState<SessionInstance | null>(null);
  const [mismatch, setMismatch] = useState<{ granted: string; current: string } | null>(null);
  /** A callback carrying a token this tab never asked for — refused rather than stored. */
  const [unsolicited, setUnsolicited] = useState(false);
  const [bootFailed, setBootFailed] = useState(false);
  /** The grant was fine and this browser would not keep it — see `storeGrant`. */
  const [storeFailed, setStoreFailed] = useState(false);
  /** A grant stored *by this page load*, which is the only thing that may cause a navigation. */
  const [granted, setGranted] = useState(false);
  const [busy, setBusy] = useState(arrival.hasToken);
  const handledRef = useRef(false);

  /*
   * The renewal option (2026-08-02), derived until touched: with no explicit choice the
   * toggle mirrors the held grant — a reader who already chose renewal sees it on for the re-grant
   * — and the reader's own press wins from then on, in either direction.
   */
  const [autoRenewChoice, setAutoRenewChoice] = useState<boolean | null>(null);
  const autoRenew = autoRenewChoice ?? (session.ready && session.facts.canSelfRenew);
  const setAutoRenew = useCallback((on: boolean) => setAutoRenewChoice(on), []);

  useEffect(() => {
    let live = true;
    void sessionInstance().then((value) => {
      if (!live) return;
      if (value === null) setBootFailed(true);
      else setInstance(value);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    // StrictMode mounts effects twice in dev, and a grant may only be consumed once.
    if (handledRef.current) return;
    handledRef.current = true;

    void (async () => {
      try {
        const captured = takeCapturedGrant();
        if (captured === null) {
          // Back from the consent screen with nothing is a denial (see PENDING_KEY). Anything else is
          // an ordinary visit, and the record is cleared either way so a stale flag cannot make the
          // *next* visit look like a refusal.
          if (arrival.pending !== null) clearPending();
          await refreshSession();
          return;
        }

        /*
         * R9 — the credential comes off the address bar **before** anything decides what to do with it,
         * so a refusal below does not leave it in history, a bookmark, a screenshot or a `Referer`.
         * `replaceState`, never a push, so Back cannot return to a URL that carries it. The existing
         * `history.state` rides along: it carries the router's scroll id, and stripping a query
         * parameter is a rewrite of this entry, not a move to another one.
         */
        globalThis.history.replaceState(globalThis.history.state, '', strippedHref(captured.href));

        if (!grantIsSolicited(arrival, nowEpochSec())) {
          // A token this tab never asked for. Not stored — see `PendingGrant.state`.
          setUnsolicited(true);
          return;
        }

        const current = await sessionInstance();
        if (current === null) {
          setBootFailed(true);
          return;
        }

        const startedOn = arrival.pending?.instanceOrigin ?? '';
        if (startedOn !== current.origin) {
          // Refused rather than stored: see `model.ts`'s note on `instanceMismatch`.
          setMismatch({ granted: startedOn, current: current.origin });
          return;
        }

        const status = await storeGrant({ token: captured.token, username: captured.username });
        if (status === 'signed-out') {
          // The write was accepted and the credential is not there: a blocked or full storage context
          // (`authStorage.write` swallows those on purpose, since a session that does not survive a
          // reload is still a session). Reported rather than shown as a successful sign-in.
          setStoreFailed(true);
          return;
        }
        setGranted(true);
      } finally {
        setBusy(false);
        clearPending();
      }
    })();
  }, [arrival]);

  /*
   * The one authenticated call, and the page's real answer to "is this working?".
   *
   * Deliberately *not* gated on the client-side scope predicate (`fetchSubscriptions` accepts `null`
   * for the granted scopes): the point here is to hear the instance say yes or no, and a page that
   * pre-refuses its own request would report a scope gap it inferred instead of one that happened.
   * Keyed by the grant's own timestamp so a fresh token is re-verified rather than reading a cached
   * verdict about the token it replaced.
   */
  const verifyKey = [AUTH_QUERY_KEY, 'verify', instance?.origin ?? '', session.grantedAtEpochSec];
  const verifyQuery = useQuery({
    queryKey: verifyKey,
    enabled: session.ready && session.facts.signedIn && instance !== null,
    queryFn: async (): Promise<VerifyOutcome> => {
      const boot = await getPlatform();
      if (!boot.ok) return { kind: 'failed', copy: bootCopy(), signedOut: false };
      const result = await fetchSubscriptions(boot.platform, null);
      if (result.ok) return { kind: 'ok', subscriptions: result.value.items.length };
      return {
        kind: 'failed',
        copy: verifyCopy(result.error),
        signedOut:
          result.error.kind === 'transport' &&
          result.error.error.kind === 'unauthorized' &&
          result.error.error.auth !== 'invalid-scope',
      };
    },
    retry: false,
    // A verification is about *now*: coming back to this page must re-ask rather than repeat itself.
    staleTime: 0,
    gcTime: 0,
  });

  /*
   * `bootFailed` is folded in here rather than at the render, and that is not cosmetic: a disabled query
   * reports `isPending` forever, so with no instance to ask, "checking…" would be the answer for the rest
   * of the page's life — and since `busy` is derived from this, every button would stay disabled on the
   * one screen able to explain the problem. One value, used by both.
   */
  const verify: VerifyOutcome = useMemo(
    () =>
      bootFailed
        ? { kind: 'failed', copy: bootCopy(), signedOut: false }
        : !session.ready || !session.facts.signedIn
          ? { kind: 'idle' }
          : verifyQuery.isPending || verifyQuery.isFetching
            ? { kind: 'checking' }
            : (verifyQuery.data ?? { kind: 'failed', copy: bootCopy(), signedOut: false }),
    [
      bootFailed,
      session.ready,
      session.facts.signedIn,
      verifyQuery.isPending,
      verifyQuery.isFetching,
      verifyQuery.data,
    ],
  );

  /*
   * Return to where the reader was going — **only after a grant made in this page load, and only once
   * the instance has accepted it.** Two deliberate restrictions:
   *
   * - Not on arrival with an existing session. `/login?next=…` reached by Back would otherwise bounce
   *   forward again, which is the trap `useResolve` documents: the reader is one press from where they
   *   were and it looks like Back is broken. With a session already in hand the page offers a *link*
   *   instead.
   * - Not before verification, and not for a session that is merely *usable*. A token can be minted
   *   and dead on arrival (0g's `expire` trap), and an `under-scoped` one can pass this particular call
   *   and still be refused by the page being returned to — so the condition is `active`, which is the
   *   only status about which the page has nothing left to say.
   */
  const returnHref = arrival.pending?.next ?? next;
  const readyToReturn =
    granted && verify.kind === 'ok' && session.facts.status === 'active' && returnHref !== null;
  useEffect(() => {
    if (readyToReturn && returnHref !== null) navigate(returnHref, { replace: true });
  }, [readyToReturn, returnHref]);

  const signIn = useCallback(() => {
    void (async () => {
      const boot = await getPlatform();
      const current = await sessionInstance();
      if (!boot.ok || current === null) {
        setBootFailed(true);
        return;
      }
      const state = newState();
      const url = buildAuthorizeUrl(boot.platform.config, {
        // R9's enumerated list, every time — plus the one opt-in the reader can add
        // (2026-08-02): the renewal scope, whose measured meaning `auth/renew.ts` records. There is
        // still no other "ask for less" or "ask for more" path: with the option off this *is* the
        // complete ask, so a re-grant after a scope gap is the same request (`auth/session.ts`).
        scopes: autoRenew ? withRenewalScope(VISIOUS_SCOPES) : VISIOUS_SCOPES,
        callbackUrl: callbackUrl(state),
        // ⚠️ An absolute epoch, not a lifetime. `authorizeExpiryEpochSec` exists so a duration cannot
        // be passed by accident — 0g measured that `expire=2592000` mints a token that died in 1970.
        expireEpochSec: authorizeExpiryEpochSec(nowEpochSec(), DEFAULT_TOKEN_LIFETIME_SEC),
      });
      if (url === null) {
        setBootFailed(true);
        return;
      }
      writePending({
        instanceOrigin: current.origin,
        next: returnHref,
        state,
        startedAtEpochSec: nowEpochSec(),
      });
      /*
       * Deliberately **not** `setBusy(true)` here. The navigation is immediate, so the flash buys
       * nothing — and a document restored from the back/forward cache comes back with the state it left
       * with, so a busy flag set on the way out would return with every control on this page disabled
       * and no event to clear it.
       */
      globalThis.location.assign(url);
    })();
  }, [autoRenew, returnHref]);

  const signOut = useCallback(() => {
    void (async () => {
      setBusy(true);
      try {
        // Dropping the authenticated query cache is *not* done here: `onSessionLost` in `main.tsx`
        // does it for every way a session can end, including one that happens in another tab.
        await signOutLocally();
        setGranted(false);
        setMismatch(null);
        setUnsolicited(false);
        setStoreFailed(false);
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const recheck = useCallback(() => {
    void refreshSession();
    void queryClient.invalidateQueries({ queryKey: [AUTH_QUERY_KEY] });
  }, [queryClient]);

  const view = useMemo(
    () =>
      loginView({
        ready: session.ready && !bootFailed,
        facts: session.facts,
        grantedAtEpochSec: session.grantedAtEpochSec,
        nowEpochSec: nowEpochSec(),
        instanceHost: instance?.host ?? null,
        denied: !arrival.hasToken && arrival.pending !== null,
        callbackProblem: arrival.problem,
        instanceMismatch: mismatch,
        unsolicited,
        storeFailed,
        returnHref,
        verify,
        autoRenew,
      }),
    [arrival, autoRenew, bootFailed, instance, mismatch, returnHref, session, storeFailed, unsolicited, verify],
  );

  return {
    view,
    busy: busy || verify.kind === 'checking',
    handlers: { signIn, signOut, recheck, setAutoRenew },
  };
}

function bootCopy(): ProblemCopy {
  return text().login.noInstance;
}

/**
 * The verification's failure, in words. The generic taxonomy handles most of it; what this adds is the
 * five meanings the instance packs into one `403` — which is the whole reason `classifyAuthFailure`
 * exists, surfaced on the page where a reader can act on it.
 */
function verifyCopy(problem: AuthApiProblem): ProblemCopy {
  // The `out-of-scope` sentence moved to `api/problem.ts` on its second caller — the
  // subscription feed hits the identical case, and the shared version names the method and endpoint.
  const words = text().login.verify;
  return describeAuthProblem(problem, {
    notFound: words.notFound,
    unreadable: () => ({
      title: words.unreadableTitle,
      detail: words.unreadableDetail,
      retryable: true,
    }),
    disabled: words.disabled,
  });
}
